/**
 * Base entity model. All Apollon models should inherit this class.
 *
 * This file is part of Minasa
 * (c) Kaden und Partner AG
 */

import { Model, Response } from 'vue-mc';
import { instance as apiInstance } from '@/helpers/api';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

export default class BaseModel extends Model {
    static fromData(data: any) {
        if (data instanceof this) {
            return data;
        } else if (data) {
            return new this(data);
        } else {
            return null;
        }
    }

    /**
     * Transient props will not be considered while saving:
     * those values do not
     * @returns
     */
    transientProps(): string[] {
        return [];
    }

    /**
     * Override in child classes: This is the API endpoint's
     * entity name (e.g. like in /services/User/list --> User)
     *
     * @return {String} The API endpoint's Entity name
     */
    entityName(): string {
        return this.constructor.name;
    }

    /**
     * Returns all data that needs to be saved (e.g. dirty props),
     * without the transient props.
     */
    getSaveData(): Record<string, any> {
        const attrs = super.getSaveData();
        const persistedAttrs: Record<string, any> = {};
        const transientProps = this.transientProps();
        for (const prop of Object.keys(attrs)) {
            if (transientProps.includes(prop) !== true) {
                persistedAttrs[prop] = attrs[prop];
            }
        }
        return persistedAttrs;
    }

    routes() {
        // Note that we use relative URLs here - we use a pre-configured instance
        // of axios, which has a baseUrl set.
        const className = this.entityName();
        const idName = this.getIdName();
        return {
            fetch: `/${className}/get?${idName}={${idName}}`,
            save: `/${className}/store`,
            delete: `/${className}/destroy?${idName}={${idName}}`,
        };
    }

    options() {
        return {
            identifier: 'id',
            validateRecursively: false,
            patch: true,
            methods: {
                fetch: 'GET',
                save: 'POST',
                update: 'POST',
                create: 'POST',
                delete: 'DELETE',
                patch: 'POST',
            },
        };
    }

    getIdName() {
        return this.getOption('identifier');
    }

    getId() {
        return this[this.getIdName()];
    }

    isDirty(): boolean {
        const props = this.changed();
        if (props) {
            for (const p of props) {
                if (this.get(p) !== this.saved(p)) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Like isDirty(), but only operates on non-transient properties (that is, props not returned
     * by the transientProps() method)
     * @returns
     */
    isDirtyForPersistedProps(): boolean {
        const props = this.getSaveData();
        if (props) {
            for (const k of Object.keys(props)) {
                if (this.get(k) !== this.saved(k)) {
                    return true;
                }
            }
        }
        return false;
    }

    isPhantom(): boolean {
        const idName = this.getIdName();
        return !(this.saved(idName) > 0);
    }

    hasUnsavedData(): boolean {
        return this.isPhantom() || this.isDirtyForPersistedProps();
    }

    listDependenciesUrl() {
        return `/${this.entityName()}/listDependencies`;
    }

    /**
     * Override original method:
     *
     * vue-mc has no possibility to use an existing axios (pre-configured) instance.
     * So we mimic a vue-mc "request" here: Request just needs a send()-Method, which we will
     * create here:
     */
    createRequest(config: AxiosRequestConfig) {
        config.headers = Object.assign(apiInstance().defaults.headers, config.headers);
        return {
            send: () =>
                apiInstance()
                    .request(config)
                    .then((response: AxiosResponse) => new Response(response)),
        };
    }

    async listDependencies(options?: { [key: string]: any } | null): Promise<Array<any>> {
        const ids = [this.getId()];
        options = {
            ids,
            ...options,
        };

        const response = await apiInstance().post(this.listDependenciesUrl(), options, {});

        return response.data.dependencies;
    }
}
